import React from "react";
import JSZip from "jszip";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { formatFilmDayList, toDayList } from "./filmWindow";
import { Document, Image, Link, Page, StyleSheet, Text, View, pdf } from "@react-pdf/renderer";
import axiosInstant from "../api";
import {
    TdcPdfHeader,
    TdcPdfSectionHeading,
    downloadPdfBlob,
    pdfExportedStamp,
    pdfFileName,
    tdcPdfStyles,
} from "./pdf/tdcPdfTheme";
import { GOLD } from "../components/PdpAudit/PdpAuditPdf";

// Expert confirmation details export (TDC-1700): a PDF of the expert's
// answers, zipped with their confirmation video and location photos when
// there are any. Reads the same admin endpoint as the confirmation page.
// The media are public S3 URLs on a bucket with GET CORS open, so they are
// fetched straight from the browser.

const styles = StyleSheet.create({
    page: tdcPdfStyles.page,
    field: tdcPdfStyles.entry,
    label: tdcPdfStyles.entryLabel,
    value: tdcPdfStyles.entryValue,
    link: { ...tdcPdfStyles.entryValue, color: GOLD, textDecoration: "underline" },
    photoGrid: { flexDirection: "row", flexWrap: "wrap", marginTop: 4 },
    photo: { width: 240, height: 180, objectFit: "contain", marginRight: 12, marginBottom: 12 },
});

interface Field {
    label: string;
    value: string;
    href?: string;
}

type Media = { name: string; blob: Blob };
type Photo = Media & { dataUrl: string | null };

const text = (value: unknown): string => {
    if (value === undefined || value === null || value === "") return "-";
    return String(value);
};

const yesNo = (value: unknown): string => {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return "-";
};

const dateRange = (dates: string[] | null | undefined): string => {
    if (!dates || dates.length === 0) return "-";
    const first = dayjs(dates[0]).format("MMM D, YYYY");
    if (dates.length === 1) return first;
    return `${first} – ${dayjs(dates[dates.length - 1]).format("MMM D, YYYY")}`;
};

const dayList = (dates: string[] | null | undefined): string =>
    formatFilmDayList(toDayList(dates)) || "-";

const joinParts = (parts: (string | null | undefined)[]): string => {
    const filled = parts.filter((part) => !!part && String(part).trim() !== "");
    return filled.length > 0 ? filled.join(", ") : "-";
};

const responseText = (c: any): string => {
    if (!c) return "No response yet";
    if (c.interested === true) return "Opted in";
    if (c.interested === false) return "Opted out";
    return "Pending";
};

const expertName = (expert: any): string =>
    [expert?.firstName, expert?.lastName].filter(Boolean).join(" ") || expert?.displayName || "Expert";

const fileExtension = (url: string, blob: Blob, fallback: string): string => {
    const fromUrl = url.split("?")[0].match(/\.([a-z0-9]{2,5})$/i);
    if (fromUrl) return fromUrl[1].toLowerCase();
    const fromType = blob.type.split("/")[1];
    return fromType ? fromType.split(";")[0] : fallback;
};

// Video uploads were always named .webm (even MP4/MOV files), so trust the
// container's magic bytes over the stored name.
const sniffVideoExtension = async (blob: Blob): Promise<string | null> => {
    const head = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    if (head[0] === 0x1a && head[1] === 0x45 && head[2] === 0xdf && head[3] === 0xa3) return "webm";
    const box = String.fromCharCode(...Array.from(head.slice(4, 8)));
    if (box !== "ftyp") return null;
    return String.fromCharCode(...Array.from(head.slice(8, 12))) === "qt  " ? "mov" : "mp4";
};

const fetchBlob = async (url: string): Promise<Blob | null> => {
    try {
        const res = await fetch(url, { mode: "cors" });
        if (!res.ok) return null;
        return await res.blob();
    } catch {
        return null;
    }
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result || ""));
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });

// @react-pdf only decodes JPEG and PNG; anything else (webp, heic) is still
// zipped, just not embedded in the PDF.
const embeddable = (blob: Blob): boolean => /^image\/(jpe?g|png)$/i.test(blob.type);

const Section: React.FC<{ label: string; fields: Field[] }> = ({ label, fields }) => (
    <>
        <TdcPdfSectionHeading label={label} />
        {fields.map((field) => (
            <View key={field.label} style={styles.field} wrap={false}>
                <Text style={styles.label}>{field.label}</Text>
                {field.href ? (
                    <Link src={field.href} style={styles.link}>
                        {field.value}
                    </Link>
                ) : (
                    <Text style={styles.value}>{field.value}</Text>
                )}
            </View>
        ))}
    </>
);

interface PdfProps {
    data: any;
    photos: Photo[];
    videoFileName: string | null;
}

const ConfirmationDetailsPdf: React.FC<PdfProps> = ({ data, photos, videoFileName }) => {
    const c = data.confirmation || {};
    const project = data.project || {};
    // Saved project shipping, else the expert's profile address — the same
    // address the expert's opt-in form shows (API resolveExpertShippingAddress).
    const address = data.expertAddress || {};
    const brand = (project.brands || []).find((b: any) => !b.rmn) || (project.brands || [])[0];
    const products = (project.products || []).map((p: any) => p.title).filter(Boolean);

    const projectFields: Field[] = [
        { label: "Project", value: text(project.title) },
        { label: "Brand", value: text(brand?.name) },
        { label: "Product", value: products.length > 0 ? products.join(", ") : "-" },
        { label: "Content Type", value: text(project.content_type?.name) },
        { label: "Film Date", value: project.film_date ? dayjs(project.film_date).format("MMM D, YYYY") : "-" },
        { label: "Filming Location", value: text(project.filming_location_text) },
    ];

    const responseFields: Field[] = [
        { label: "Response", value: responseText(data.confirmation) },
        { label: "Submitted", value: c.confirmation_status === "submitted" ? "Yes" : "No" },
        ...(c.interested === false ? [{ label: "Decline Reason", value: text(c.decline_reason) }] : []),
        { label: "Notes", value: text(c.interest_notes) },
        { label: "Expert Rate", value: c.rate ? `$${Number(c.rate).toLocaleString()}` : "-" },
    ];

    const availabilityFields: Field[] = [
        { label: "Preferred Dates", value: dayList(c.preferred_dates) },
        { label: "Unavailable Dates", value: dateRange(c.unavailable_dates) },
        { label: "Confirmed Film Date", value: yesNo(c.user_confirmed) },
        { label: "Willing to Travel", value: yesNo(c.willing_to_travel) },
    ];

    const productFields: Field[] = [
        { label: "Uses the Product", value: yesNo(c.uses_product) },
        { label: "Product Experience", value: text(c.product_experience) },
        { label: "Product Received", value: yesNo(c.product_received) },
        {
            label: "Shipping Address",
            value: joinParts([address.address1, address.address2, address.city, address.state, address.zipcode]),
        },
    ];

    const locationFields: Field[] = [
        { label: "Willing to Film at a Location", value: yesNo(c.willing_to_film_at_location) },
        {
            label: "Filming Address",
            value: joinParts([c.filming_address, c.filming_city, c.filming_state, c.filming_zip]),
        },
    ];

    const videoFields: Field[] = c.video_path
        ? [
              {
                  label: videoFileName ? `Included in this download as ${videoFileName}` : "Video",
                  // Link text, not the raw URL: react-pdf never wraps a
                  // space-less URL, so a long S3 key would run off the page.
                  value: "Open the original video",
                  href: c.video_path,
              },
          ]
        : [{ label: "Video", value: "No video submitted" }];

    return (
        <Document>
            <Page size="LETTER" style={styles.page}>
                <TdcPdfHeader
                    title={expertName(data.expert)}
                    subtitle={text(project.title)}
                    meta={pdfExportedStamp("EXPERT CONFIRMATION DETAILS")}
                />
                <Section label="Project" fields={projectFields} />
                <Section label="Response" fields={responseFields} />
                <Section label="Availability" fields={availabilityFields} />
                <Section label="Product & Shipping" fields={productFields} />
                <Section label="Filming Location" fields={locationFields} />
                <Section label="Confirmation Video" fields={videoFields} />
                {photos.length > 0 && (
                    <>
                        <TdcPdfSectionHeading label="Location Photos" />
                        <View style={styles.photoGrid}>
                            {photos.map((photo) =>
                                photo.dataUrl ? (
                                    <Image key={photo.name} src={photo.dataUrl} style={styles.photo} />
                                ) : (
                                    <Text key={photo.name} style={styles.value}>
                                        {`${photo.name} (included in this download)`}
                                    </Text>
                                ),
                            )}
                        </View>
                    </>
                )}
            </Page>
        </Document>
    );
};

const buildAndDownload = async (projectId: number | string, userId: number | string) => {
    const { data } = await axiosInstant.get(`/admin/brand-projects/${projectId}/experts/${userId}/confirmation`);
    if (!data?.confirmation) {
        toast.error("This expert hasn't submitted confirmation details yet.");
        return;
    }
    const c = data.confirmation;
    const baseName = pdfFileName(expertName(data.expert), "confirmation", "expert").replace(/\.pdf$/i, "");

    let missing = 0;
    let video: Media | null = null;
    if (c.video_path) {
        const blob = await fetchBlob(c.video_path);
        if (blob) {
            const ext = (await sniffVideoExtension(blob).catch(() => null)) || fileExtension(c.video_path, blob, "webm");
            video = { name: `${baseName}_video.${ext}`, blob };
        }
        else missing += 1;
    }

    const imageUrls: string[] = Array.isArray(c.location_images) ? c.location_images.filter(Boolean) : [];
    const fetched = await Promise.all(
        imageUrls.map(async (url, index): Promise<Photo | null> => {
            const blob = await fetchBlob(url);
            if (!blob) return null;
            return {
                name: `photo_${index + 1}.${fileExtension(url, blob, "jpg")}`,
                blob,
                dataUrl: embeddable(blob) ? await blobToDataUrl(blob).catch(() => null) : null,
            };
        }),
    );
    const photos = fetched.filter((photo): photo is Photo => !!photo);
    missing += fetched.length - photos.length;

    const pdfBlob = await pdf(
        <ConfirmationDetailsPdf data={data} photos={photos} videoFileName={video ? video.name : null} />,
    ).toBlob();

    if (missing > 0) {
        toast.error(`${missing} file${missing > 1 ? "s" : ""} could not be added to the download.`);
    }

    if (!video && photos.length === 0) {
        downloadPdfBlob(pdfBlob, `${baseName}.pdf`);
        return;
    }

    const zip = new JSZip();
    zip.file(`${baseName}.pdf`, pdfBlob);
    if (video) zip.file(video.name, video.blob);
    if (photos.length > 0) {
        const folder = zip.folder("photos");
        photos.forEach((photo) => folder?.file(photo.name, photo.blob));
    }
    downloadPdfBlob(await zip.generateAsync({ type: "blob" }), `${baseName}.zip`);
};

// Never rejects: failures surface as a toast so card buttons can just await it.
export const downloadConfirmationDetails = async (projectId: number | string, userId: number | string) => {
    try {
        await buildAndDownload(projectId, userId);
    } catch (e) {
        console.error(e);
        toast.error("Couldn't download the confirmation details. Try again.");
    }
};
